
import { defineComponent } from "vue";
import PropSelector from "./propSelector.vue";
import ColorPicker from "./colorPicker.vue";
import ColorMapSelector from "./colorMapSelector.vue";
import FieldRange from "./fieldRange.vue";
import { useDisplayPaneStore } from "@/stores/ssaStore/displayPane";
import { storeToRefs } from "pinia";
import { useSsaStore } from "@/stores/ssaStore";
import { useActiveStore } from "@/stores/ssaStore/active";
import { usePresetsStore } from "@/stores/ssaStore/presets";
import { useLayerStore } from "@/stores/ssaStore/layer";
import { usePvwStore } from "@/stores/ssaStore/pvw";
// import Discretize from "./discretize.vue";
import { hex2float } from "vtk.js/Sources/Common/Core/Math";
import Histogram from "./histogram.vue";
import IndexedColors from "./indexedColors.vue";
import PicksColorPicker from "./picksColorPicker.vue";
import { useBusyStore } from "@/stores/ssaStore/busy";
import { useDebounceFn } from "@vueuse/core";
import { usePickingStore } from "@/stores/ssaStore/picking";
import { useFiltersStore } from "@/stores/ssaStore/filters";
import { hexToRgb, rgbToHex } from "@/utils/ColorUtils";
import { PropertyDefaults, PropertySettings } from "@/types/dataset";
const DEFAULT_PRESET_IMAGE = {
	image: new Image(),
	minColor: "#fff",
	maxColor: "#fff"
};
const DISCRETE_COLORS = [
	hex2float("#7e1e9c"), // purple
	hex2float("#15b01a"), // green
	hex2float("#0343df"), // blue
	hex2float("#ff81c0"), // pink
	hex2float("#e50000"), // red
	hex2float("#95d0fc"), // light blue
	hex2float("#7e1e9c"), // purple
	hex2float("#15b01a"), // green
	hex2float("#0343df"), // blue
	hex2float("#ff81c0"), // pink
	hex2float("#e50000"), // red
	hex2float("#95d0fc")
];
const COLOR_TYPE_LABELS = {
	Continuous: "Linear",
	Logarithmic: "Logarithmic",
	Discrete: "Discrete",
	Categorical: "Categorical"
};
const LOG_VALUE = "Logarithmic";

/************************************
 * ioGAS specific constants
 */
// Name of the property that will is used to store the
// ioGAS attribute indices inside data.csv in the .gas file
export const GAS_COLOR_PROPERTY_STR = "__gas__color__";
// TODO find a better way of defining ioGAS attributes
const GAS_COLOR_PROPERTY: { label: string; value: string; type: "categorical" | "continuous" } = {
	label: "ioGAS Colors",
	value: GAS_COLOR_PROPERTY_STR,
	type: "categorical"
};
/**************************************/

class GradientStop {
	active = false;
	color = "#ffffff";
	stop = 0;
	id = "";
	constructor(color: string, stop: number) {
		this.color = color;
		this.stop = stop;
		const guidGenerator = () => {
			const S4 = () => {
				return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
			};
			return S4() + S4() + "-" + S4() + "-" + S4() + "-" + S4() + "-" + S4() + S4() + S4();
		};
		this.id = guidGenerator();
	}
	getRbg() {
		const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(this.color);
		return {
			r: parseInt(result![1], 16),
			g: parseInt(result![2], 16),
			b: parseInt(result![3], 16)
		};
	}
}
const sleep = (ms: number) => {
	return new Promise((resolve) => setTimeout(resolve, ms));
};
// interface PvPropertyColumn {
// 	value: string;
// 	type?: string;
// 	label: string;
// 	children?: PvPropertyColumn[];
// }
export default defineComponent({
	name: "imdex-color-by-selector",
	components: {
		PropSelector,
		ColorPicker,
		ColorMapSelector,
		FieldRange,
		// Discretize,
		Histogram,
		IndexedColors,
		PicksColorPicker
	},
	setup() {
		const pickStore = usePickingStore();
		const displayPaneStore = useDisplayPaneStore();
		const {
			SSA_ACTIVE_LAYER: activeLayer,
			SSA_ACTIVE_DATASET: activeDataset,
			SSA_ACTIVE_LAYER_STATE: activeLayerState,
			SSA_ACTIVE_REPRESENTATION_STATE: activeRepresentationState
		} = storeToRefs(useActiveStore());
		const storeRef = storeToRefs(displayPaneStore);
		const setIsDiscrete = displayPaneStore.SET_DISCRETE;
		const { SSA_REPRESENTATION_STATES: representationStates } = storeToRefs(useLayerStore());
		const ssaStore = useSsaStore();
		const activeStore = useActiveStore();
		const presetsStore = usePresetsStore();
		const { colorScale } = storeToRefs(usePresetsStore());
		const { SSA_SET_COLOR_SCALE: setColorScale } = usePresetsStore();
		const { BUSY_IS: isBusy } = storeToRefs(useBusyStore());
		const pvwStore = usePvwStore();
		const toAnnotationValue = (n: any) => {
			return (Math.round(n * 100) / 100).toString();
		};
		const { view: activeView } = storeToRefs(useActiveStore());
		const toItemsWithText = (list: any, map: any) => {
			return list.map((value: any) => ({ value, text: map[value] || value }));
		};
		const { SSA_ALL_ACTIVE_FILTERS: allActiveFilters } = storeToRefs(useFiltersStore());
		const filtersStore = useFiltersStore();
		return {
			activeRepresentationState,
			colorScale,
			setColorScale,
			pickStore,
			pvwStore,
			activeDataset,
			activeLayer,
			activeLayerState,
			activeView,
			displayPaneStore,
			setIsDiscrete,
			storeRef,
			ssaStore,
			activeStore,
			presetsStore,
			toAnnotationValue,
			toItemsWithText,
			fetchHistogram: ssaStore.SSA_HISTOGRAM_FETCH,
			isBusy,
			representationStates,
			allActiveFilters,
			filtersStore
		};
	},
	props: {
		hierarchyLayerSelections: {
			type: Object,
			default: () => ({}) as any
		},
		viewId: {
			type: String,
			default: ""
		},
		fileName: {
			type: String,
			default: ""
		},
		layerName: {
			type: String,
			default: ""
		},
		config: {
			type: Object,
			default: () => ({}) as any
		},
		active: {
			type: Boolean,
			default: false
		},
		datasetType: {
			type: String,
			default: "drillhole"
		},
		percentile: {
			type: Number,
			default: 5
		},
		visualized: {
			type: Boolean,
			default: false
		},
		open: {
			type: Boolean,
			default: false
		}
	},
	data: (vm: any) => {
		return {
			isReady: false,
			colorTypeItems: vm.toItemsWithText(["Continuous"], COLOR_TYPE_LABELS),
			refreshKey: 0,
			localAnnotations: null,
			opacity: 0,
			size: 60,
			propSelectorOpen: false,
			colorPickerOpen: false,
			solidColorPickerOpen: false,
			colorMapSelectorOpen: false,
			outsideClickListener: null,
			activeColor: "#ff9900",
			activeSolidColor: "#ffffff",
			activeDiscreteBlock: null,
			localColorByState: null as any,
			customColorAnnotation: null as any,
			histogramData: [] as any,
			histogramRemoved: [0, 0] as any,
			updatingHistogram: false,
			displayPaneWidth: 256,
			annotationTracking: {} as any,
			tLayerOpacity: 100,
			tLayerPointSize: 3,
			tLayerLineWidth: 1,
			maxPointSize: 18,
			updating: "",
			shiftListener: null as any,
			applyingPropertySettings: false
		};
	},
	mounted() {
		if (this.activeRepresentationState != null) {
			this.isReady = true;
		}
	},
	computed: {
		fieldList() {
			return [
				...(this.config?.availableProperties?.[0]?.children?.map((item: any) => {
					return item.value;
				}) ?? []),
				"easting",
				"northing",
				"elevation"
			];
		},
		cellOutline: {
			get() {
				return (
					this.remoteColorByState.geometry &&
					this.remoteColorByState.geometry.Representation == "Surface With Edges"
				);
			},
			set() {
				this.setEdgeVisibility(!this.cellOutline);
			}
		},
		layerOpacity: {
			get() {
				return this.updating == "opacity"
					? this.tLayerOpacity
					: Math.round(
							this.remoteColorByState.geometry &&
								this.remoteColorByState.geometry.Opacity * 100
					  ).toFixed(0);
			},
			set(v: any) {
				this.tLayerOpacity = v;

				// mpd--4499 work-around - unopen (inactive) layers on load - necessary for quick swapping between layers on initial load
				if (
					this.applyingPropertySettings &&
					this.activeLayer !== this.tempConfig?.layerName &&
					typeof v === "number"
				) {
					this.ssaStore.SSA_REPRESENTATION_UPDATE_PROPERTY({
						layerName: (this.config as any).layerName,
						viewId: this.activeStore.view,
						type: "geometry",
						name: "Opacity",
						value: v / 100
					});
				}
				// end of work-around (else is base case, to prevent calling the update twice)
				else {
					this.setLayerOpacity(v, this);
				}
				// mpd-4499 - property value tracking for layer level properties
				if (!this.applyingPropertySettings) {
					// only reassign on adjusting opacity, not when applying property settings for the next property
					this.displayPaneStore.SSA_SET_PROPERTY_SETTING(
						this.config?.layerName,
						this.tempConfig?.selectedProperty?.value,
						{ opacity: v }
					);
				}
			}
		},
		layerPointSize: {
			get() {
				return this.updating == "pointSize"
					? this.tLayerPointSize
					: this.remoteColorByState.geometry &&
							this.remoteColorByState.geometry.PointSize;
			},
			set(v: any) {
				this.tLayerPointSize = v;

				// mpd--4499 work-around - unopen (inactive) layers on load - necessary for quick swapping between layers on initial load
				if (
					this.applyingPropertySettings &&
					this.activeLayer !== this.tempConfig?.layerName &&
					typeof v === "number"
				) {
					this.ssaStore.SSA_REPRESENTATION_UPDATE_PROPERTY({
						layerName: (this.config as any).layerName,
						viewId: this.activeStore.view,
						type: "geometry",
						name: "PointSize",
						value: v
					});
				} // end of work-around (else is base case, to prevent calling the update twice)
				else {
					this.setLayerPointSize(v, this);
				}
				// mpd-4499 - property value tracking for layer level properties
				if (!this.applyingPropertySettings) {
					// only reassign on adjusting opacity, not when applying property settings for the next property
					this.displayPaneStore.SSA_SET_PROPERTY_SETTING(
						this.config?.layerName,
						this.tempConfig?.selectedProperty?.value,
						{ pointSize: v }
					);
				}
			}
		},
		layerLineWidth: {
			get() {
				return this.updating == "lineWidth"
					? this.tLayerLineWidth
					: this.remoteColorByState.geometry &&
							this.remoteColorByState.geometry.LineWidth;
			},
			set(v: any) {
				this.tLayerLineWidth = v;

				// mpd--4499 work-around - unopen (inactive) layers on load - necessary for quick swapping between layers on initial load
				if (
					this.applyingPropertySettings &&
					this.activeLayer !== this.tempConfig?.layerName &&
					typeof v === "number"
				) {
					this.ssaStore.SSA_REPRESENTATION_UPDATE_PROPERTY({
						layerName: (this.config as any).layerName,
						viewId: this.activeStore.view,
						type: "geometry",
						name: "LineWidth",
						value: v
					});
				} // end of work-around (else is base case, to prevent calling the update twice)
				else {
					this.setLayerLineWidth(v, this);
				}
				// mpd-4499 - property value tracking for layer level properties
				if (!this.applyingPropertySettings) {
					// only reassign on adjusting opacity, not when applying property settings for the next property
					this.displayPaneStore.SSA_SET_PROPERTY_SETTING(
						this.config?.layerName,
						this.tempConfig?.selectedProperty?.value,
						{ lineWidth: v }
					);
				}
			}
		},
		annotations() {
			return this.remoteColorByState && this.remoteColorByState.coloring.annotations;
		},
		isPick() {
			return this.config?.pickProperty != null;
		},
		isValid() {
			return this.histogramData && this.histogramDataRange && this.histogramData.length > 1;
		},
		totalHistogramCount() {
			if (this.isValid) {
				return this.histogramData.reduce((a: any, b: any) => a + b, 0);
			}
			return 0;
		},
		percentileCount() {
			return Math.round(this.totalHistogramCount * this.percentile * 0.01);
		},
		histogramBinWidth() {
			if (!this.isValid) {
				return 0.1;
			}
			const [min, max] = this.histogramDataRange;
			const nbBins = this.histogramData.length;
			return (max - min) / (nbBins - 1);
		},
		minMagicRange() {
			if (!this.isValid) {
				return 0;
			}
			let count = 0;
			let binIndex = 0;

			while (count < this.percentileCount) {
				count += this.histogramData[binIndex];
				binIndex++;
			}
			if (count > 1.5 * this.percentileCount) {
				binIndex--;
			}

			return this.histogramDataRange[0] + binIndex * this.histogramBinWidth;
		},
		maxMagicRange() {
			if (!this.isValid) {
				return 0;
			}
			let count = 0;
			let binIndex = this.histogramData.length - 1;

			while (count < this.percentileCount) {
				count += this.histogramData[binIndex];
				binIndex--;
			}
			if (count > 1.5 * this.percentileCount) {
				binIndex++;
			}

			return this.histogramDataRange[0] + binIndex * this.histogramBinWidth;
		},
		// Dex's original declaration
		// tempConfig: {opactity:0, pointSize:0,cellOutline:false,layerType:'point', isDiscrete:false,categories:[],colorMap:[],discreteColors:[],scaleType:'linear' },
		tempConfig: {
			get() {
				return this.config;
			},
			set(value: any) {
				this.$emit("update:config", value);
			}
		},
		// -- Histogram
		useLogScale() {
			return !!(
				this.activeRepresentationState &&
				this.activeRepresentationState.coloring &&
				this.activeRepresentationState.coloring.useLogScale
			);
		},
		showHistogram() {
			return (
				!this.isCategorical &&
				this.colorBy.field &&
				this.histogramData &&
				this.histogramData.length &&
				this.histogramDataRange[1] > this.histogramDataRange[0]
			);
		},
		histogramDataRange() {
			// If we filter that layer with the same field we colorBy
			const { field } = this.colorBy;
			const threshold = ((this.activeLayerState && this.activeLayerState.filters) || []).find(
				({ Scalars }: { Scalars: any }) => Scalars[1] === field
			);
			if (threshold) {
				return threshold.ThresholdRange;
			}
			return this.getFieldRange(this.colorBy.field);
		},
		histogramColorRange() {
			return this.colorBy.colorRange;
		},
		colorColumn() {
			const { field } = this.remoteColorByState.coloring;
			return this.presetsStore.SSA_FIELD_COLUMN_GET(field);
		},
		numberOfDiscreteColors() {
			return Math.floor(Math.max(this.remoteColorByState.coloring.annotations.length / 2, 3));
		},
		disableSelector() {
			return this.useGASColors;
		},
		preset() {
			return this.presetsStore.SSA_PRESETS_ITEMS[
				this.presetsStore.SSA_PRESETS_ITEMS.findIndex((item: any) => {
					const presetCheck = this.remoteColorByState.coloring.presetName;
					return item.id == presetCheck;
				})
			];
		},
		presetName: {
			get() {
				if (
					this.presetsStore.SSA_PRESETS_ITEMS[
						this.presetsStore.SSA_PRESETS_ITEMS.findIndex((item: any) => {
							return item.id == this.remoteColorByState.coloring.presetName;
						})
					] == null
				) {
					return {
						id: "",
						text: "",
						value: "",
						rgbPoints: [0, 0, 0, 1, 1, 1, 0, 1],
						annotations: []
					} as {
						id: string;
						text: string;
						value: string;
						rgbPoints: number[];
						annotations?: string[];
					}; // # probably missing some properties
				}
				let idCheck =
					this.remoteColorByState.coloring.preset &&
					this.remoteColorByState.coloring.preset.id;
				if (idCheck == null) {
					idCheck = this.remoteColorByState.coloring.presetName || "Rainbow Uniform";
				}
				if (idCheck == "") {
					idCheck = "Rainbow Uniform";
				}

				return {
					type: this.presetsStore.SSA_PRESETS_ITEMS[
						this.presetsStore.SSA_PRESETS_ITEMS.findIndex((item: any) => {
							return item.id == idCheck;
						})
					].type,
					id: this.presetsStore.SSA_PRESETS_ITEMS[
						this.presetsStore.SSA_PRESETS_ITEMS.findIndex((item: any) => {
							return item.id == idCheck;
						})
					].id,
					text: this.presetsStore.SSA_PRESETS_ITEMS[
						this.presetsStore.SSA_PRESETS_ITEMS.findIndex((item: any) => {
							return item.id == idCheck;
						})
					].text,
					rgbPoints:
						this.presetsStore.SSA_PRESETS_ITEMS[
							this.presetsStore.SSA_PRESETS_ITEMS.findIndex((item: any) => {
								return item.id == idCheck;
							})
						].rgbPoints,
					annotations:
						this.presetsStore.SSA_PRESETS_ITEMS[
							this.presetsStore.SSA_PRESETS_ITEMS.findIndex((item: any) => {
								return item.id == idCheck;
							})
						].annotations,
					value: this.presetsStore.SSA_PRESETS_ITEMS[
						this.presetsStore.SSA_PRESETS_ITEMS.findIndex((item: any) => {
							return item.id == idCheck;
						})
					].value
				};
			},
			set(presetName: any) {
				this.$nextTick(() => {
					let min = (this.colorBy.colorRange && this.colorBy.colorRange[0]) || 0;
					let max = (this.colorBy.colorRange && this.colorBy.colorRange[1]) || 1;
					const an = presetName.annotations;
					if (presetName.type == "fixed") {
						min = presetName.rgbPoints[0] as number;
						max = presetName.rgbPoints[presetName.rgbPoints.length - 4] as number;
					}
					const obj = {
						type: presetName.type,
						presetName: presetName.id,
						preset: presetName,
						rgbPoints: presetName.rgbPoints,
						presetInvert: false,
						colorRange: [min, max]
					} as any;

					if (an && an.length > 0) {
						obj.annotations = an;
					}
					this.colorBy = obj;
					this.$nextTick(this.colorByPush);
				});
			}
		},
		presetIsFixed() {
			return (
				this.remoteColorByState &&
				this.remoteColorByState.coloring &&
				this.presetsStore.fixedColorPresets
					.map((item: any) => item.id)
					.indexOf(this.remoteColorByState.coloring.presetName) > -1
			);
		},
		fieldRanges() {
			if (this.activeLayerState == null) return {};
			if (this.activeLayerState.fields != null) {
				return (this.activeLayerState && this.activeLayerState.fields) || {};
			} else {
				return (this.activeLayerState && this.activeLayerState.fields) || {};
			}
		},
		gradient() {
			this.refreshKey;
			const rgbPoints =
				this.remoteColorByState.coloring.rgbPoints &&
				this.remoteColorByState.coloring.rgbPoints.length
					? this.remoteColorByState.coloring.rgbPoints
					: [];
			const stops = [] as any;

			const min = rgbPoints[0] as number;
			const max = rgbPoints[rgbPoints.length - 4] as number;

			for (let i = 0; i < rgbPoints.length; i += 4) {
				const [val, r, g, b] = [
					rgbPoints[i],
					rgbPoints[i + 1],
					rgbPoints[i + 2],
					rgbPoints[i + 3]
				] as any;
				const stop = new GradientStop(
					rgbToHex(Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)),
					((val - min) / (max - min)) * 100
				) as any;
				stops.push(stop);
			}
			return stops;
		},
		evaluatedRgbPoints() {
			let rgbPoints = [] as number[];
			for (let i = 0; i < this.gradient.length; i++) {
				const stop = this.gradient[i].stop;
				const { r, g, b } = hexToRgb(this.gradient[i].color);
				rgbPoints = rgbPoints.concat([stop, r / 255, g / 255, b / 255]);
			}
			return rgbPoints;
		},
		colorGradient() {
			const arr = [] as string[];
			for (let i = 0; i < this.gradient.length; i++) {
				const val = `${this.gradient[i].color} ${this.gradient[i].stop + "%"}`;
				arr.push(val);
			}
			return `linear-gradient(90deg, ${arr.join(",")})`;
		},
		inverted() {
			return (
				this.remoteColorByState.coloring.presetInvert &&
				this.remoteColorByState.coloring.presetInvert
			);
		},
		colorPreset() {
			const key = `${this.remoteColorByState.coloring.presetInvert ? "invert_" : ""}${
				this.remoteColorByState.coloring.presetName
			}`;
			return this.presetsStore.SSA_PRESETS_IMAGES[key] || DEFAULT_PRESET_IMAGE;
		},
		fieldModel: {
			get() {
				if (this.colorBy.colorRange) {
					let {
						colorRange: [min, max]
					}: { colorRange: any } = this.colorBy;
					const field = this.colorBy.field;

					if (this.presetIsFixed) {
						min = this.preset.rgbPoints[0];
						max = this.preset.rgbPoints[this.preset.rgbPoints.length - 4];
					}
					return {
						field,
						min: Math.round(min * 1000) / 1000,
						max: Math.round(max * 1000) / 1000
					};
				} else {
					return {
						field: "",
						min: 0,
						max: 1
					};
				}
			},
			set({ field, min, max }: { field: string; min: number; max: number }) {
				this.colorBy = { field, colorRange: [Number(min), Number(max)] };
			}
		},
		isDiscrete: {
			get(): any {
				return this.remoteColorByState.coloring.colorType === "Discrete";
			},
			set(v: any) {
				this.colorBy = { colorType: v ? "Discrete" : "Continuous" };

				this.colorByPush().then(async () => {
					const timer: any = (i: number) => {
						// waiting for the batchlistener to populate the Pinia store getters.
						if (this.remoteColorByState.coloring.field == "" && i > 0) {
							setTimeout(async () => {
								await timer(i - 1);
								return;
							}, 100);
						} else {
							this.colorType = v ? "Discrete" : "Continuous";
							return Promise.resolve();
						}
					};
					await timer(50);
				});
			}
		},
		useGASColors: {
			get(): any {
				return this.tempConfig?.useGASAttributes;
			},
			set(v: any) {
				this.displayPaneStore.SET_USE_GAS_ATTRIBUTES(v, this.tempConfig?.layerIdentifier);

				if (v) {
					this.selectProp(GAS_COLOR_PROPERTY);
				} else {
					if (this.tempConfig?.previouslySelectedProperty) {
						this.selectProp(this.tempConfig.previouslySelectedProperty);
						this.displayPaneStore.SET_PREV_SELECTED_PROPERTY(
							null,
							this.tempConfig?.layerName
						);
					} else {
						// no longer used - kept as a base case for datasets saved before previousColorByProperty was implemented
						this.applyDefaultColorProperty(true);
					}
				}
			}
		},
		isCategorical() {
			return this.remoteColorByState.coloring.colorType === "Categorical";
		},
		isContinuous() {
			return this.remoteColorByState.coloring.colorType === "Continuous";
		},
		isLogarithmic() {
			return this.remoteColorByState.coloring.useLogScale;
		},
		colorType: {
			get(): any {
				if (
					this.remoteColorByState.coloring.useLogScale &&
					this.remoteColorByState.coloring.colorType === "Continuous"
				) {
					return LOG_VALUE;
				}
				return this.remoteColorByState.coloring.colorType;
			},
			async set(colorType: string) {
				const { layerName } = this.config as any;
				const viewId = this.activeStore.view;
				const {
					field,
					useLogScale = 0,
					applyPreset,
					annotations,
					rgbPoints
				} = this.remoteColorByState.coloring;
				this.pvwStore.PVW_SET_FIELD_LIST({ layerName, viewId, fieldList: this.fieldList });

				const state = {
					layerName,
					viewId,
					field,
					colorType,
					rgbPoints,
					useLogScale,
					applyPreset,
					annotations,
					fieldList: this.fieldList
				};
				if (colorType.toLowerCase() == "linear" && this.config?.type != "picks") {
					state.colorType = "Continuous";
				}
				// Special handling for log scale
				if (colorType.toLowerCase() === LOG_VALUE.toLowerCase()) {
					state.colorType = "Continuous";
					state.useLogScale = 1;
				} else {
					state.useLogScale = 0;
				}

				if (colorType === "Discrete") {
					// Removed because picks are no longer discrete; they now use the solid color picker.
					// await this.updateAnnotationsAndColors(
					// 	this.isPick ? 1 : this.numberOfDiscreteColor
					// );
					return;
				} else {
					state.applyPreset = true;
				}

				// This prevents an update after a categorical default property is applied for BD collars
				if (state.colorType === "Categorical" && state.annotations.length === 0) {
					return;
				}
				await this.ssaStore.SSA_COLOR_BY_UPDATE(state);
			}
		},
		customAnnotationColors() {
			if (this.isCategorical) {
				return {
					value1: {
						color: [1, 0, 1],
						label: "value 1"
					}
				};
				// const layerColors = this.categoricalColors[this.activeLayer];
				// if (layerColors && layerColors[this.colorBy.field]) {
				// const colors = layerColors[this.colorBy.field];
				// const annotationColors = {};
				// colors.forEach(({ color, label, value }) => {
				//     annotationColors[value] = { color: color, label: label };
				// });
				// return annotationColors;
				// }
			}
			return null;
		},
		remoteColorByState() {
			return (
				(this.representationStates[this.activeStore.view] &&
					this.representationStates[this.activeStore.view][this.config?.layerName] &&
					this.representationStates[this.activeStore.view][this.config?.layerName]) || {
					coloring: {
						field: "",
						layerName: this.config?.layerName,
						viewId: this.activeStore.view,
						presetName: "Rainbow Uniform",
						presetInvert: false,
						colorType: "Continuous", // or "Categorical"
						colorRange: [1, -1],
						fieldLabel: "",
						annotations: []
						// NOTE: may also contain, e.g., indexedColors, useLogScale
						// See SSA_ACTIVE_REPRESENTATION_STATE
					},
					geometry: {
						LineWidth: 0,
						PointSize: 0,
						Opacity: 1
						// May also contain Representation
					}
				}
			);
		},
		colorBy: {
			get(): any {
				return this.remoteColorByState.coloring;
			},
			set(state: any): any {
				this.localColorByState = Object.assign({}, this.localColorByState, state);
			}
		},
		isPointDiscrete: {
			get(): any {
				return this.tempConfig?.isDiscrete;
			},
			set(v: any) {
				this.setIsDiscrete(v, this.fileName);
			}
		}
	},
	watch: {
		colorBy: {
			deep: true,
			handler(v) {
				this.handleColorByChange(this, v);
			}
		},
		isReady(v) {
			if (!v) return;
			this.tempConfig = this.config;

			this.resizeContainer();

			if (this.colorColumn) {
				this.getColorType(this.colorColumn);
			}

			this.shiftListener = document.addEventListener("keydown", (e) => {
				if (e.shiftKey && this.propSelectorOpen) {
					this.propSelectorOpen = false;
				}
				if (e.shiftKey && this.colorPickerOpen) {
					this.colorPickerOpen = false;
				}
			});

			if (this.config?.hasDataLoaded && !this.config?.defaults_applied) {
				this.applyDefaultColorProperty();
			}
			if (
				this.tempConfig?.displayPaneLayerUnselectedOnSaveState &&
				this.remoteColorByState.coloring?.field === "" &&
				v === true &&
				this.remoteColorByState.layerName === this.tempConfig?.layerName &&
				this.tempConfig?.selected === true
			) {
				// (2 of 2) Fix for MPD-4062 - conditional may be excessive (only displayPaneLayerUnselectedOnSaveState) but better to have more restrictions than less
				this.handleDisplayPaneUnselectedOnSaveState();
			}

			if (
				this.config?.type == "picks" &&
				this.config?.selected &&
				this.config?.isBlastdog &&
				this.config?.hasDataLoaded &&
				!this.config?.defaults_applied
			) {
				this.applyDefaultPick();
			}
			this.applyDefaultSurface();

			this.loadDatasetState();

			this.$nextTick(() => {
				this.updateHistogram();
			});
		},
		activeRepresentationState: {
			deep: true,
			handler(v) {
				if (this.datasetType === "surface" && v?.coloring?.field === "" && this.isReady) {
					// Fix for MPD-3601 - many false positives, but doesn't seem to impact performance
					this.applyDefaultSurface();
				}

				if (
					this.tempConfig?.displayPaneLayerUnselectedOnSaveState &&
					v?.coloring?.field === "" &&
					this.isReady &&
					v?.layerName === this.config?.layerName &&
					this.tempConfig?.selected === true
				) {
					// (1 of 2) Fix for MPD-4062 - conditional may be excessive (only displayPaneLayerUnselectedOnSaveState & isReady is required) but better to have more restrictions than less
					this.handleDisplayPaneUnselectedOnSaveState();
				}
				if (v != null && !this.isReady) {
					this.isReady = true;
				}
			}
		},
		hierarchyLayerSelections: {
			deep: true,
			handler(v) {
				// should this be after the stuff below?
				// MPD-4153 - re-apply defaults if the data tree selection (hierarchy) is selected piecemeal
				// only used if no static color (this.config?.color) is used for the entire layer
				// ?? Do we need to check if the property was changed from the default? --> no, it persists the old one even though it calls this
				// BUt if it's changed to categorical, there is a problem
				let applyDefaultsForHierarchy = false;
				if (this.config?.defaults_applied && this.config?.isBlastdog && !this.config?.color)
					for (const hierarchy of v[this.activeView][this.config?.layerName]) {
						const hierarchyLevel = hierarchy.split("::");
						if (!this.config?.defaultsAppliedToHierarchy?.[hierarchyLevel[1]]) {
							applyDefaultsForHierarchy = true;
						}
					}

				console.log(
					`🚀🤗 ~ apply defaults - color by selector:`,
					applyDefaultsForHierarchy,
					this.isCategorical
				);
				if (applyDefaultsForHierarchy) this.applyDefaultColorProperty(true);
				// End of MPD-4153
				const { layerName } = this.config as any;
				const viewId = this.activeStore.view;
				this.pvwStore.PVW_SET_FIELD_LIST({ layerName, viewId, fieldList: this.fieldList });
				this.pickStore.SHOW_PICK_DATA(false);
				this.pickStore.SSA_PICK_DATA_SET([]);
				this.pickStore.SSA_PICK_DATA_STORE_SET([]);
			}
		},
		activeDataset: {
			deep: true,
			handler() {
				this.loadDatasetState();
			}
		},
		async open() {
			this.$nextTick(() => {
				this.updateHistogram();
				const { layerName } = this.config as any;
				const viewId = this.activeStore.view;
				this.pvwStore.PVW_SET_FIELD_LIST({ layerName, viewId, fieldList: this.fieldList });
			});
		},
		async visualized(v) {
			if (v) {
				this.resizeContainer();
				this.updateHistogram();

				this.tempConfig = this.config as any;

				if (this.colorColumn) {
					this.getColorType(this.colorColumn);
				}

				if (
					this.tempConfig?.pickProperty &&
					this.tempConfig?.availableProperties?.[0].children
				) {
					const pickProperty = (
						this.tempConfig as any
					).availableProperties[0].children.find((item: any) => {
						return item.value == this.tempConfig?.pickProperty;
					});

					if (pickProperty) {
						// @selectProp equivalent - might be better to refactor into a helper function
						this.displayPaneStore.SET_PROPERTY(
							pickProperty,
							this.tempConfig?.layerIdentifier
						);
						await this.setColorByField(pickProperty);
						this.isDiscrete = true;
					}
					const { layerName } = this.config as any;
					const viewId = this.activeStore.view;
					this.setActiveSolidColor();
					this.pvwStore.PVW_SET_FIELD_LIST({
						layerName,
						viewId,
						fieldList: this.fieldList
					});
				}
			}
		},
		config: {
			deep: true,
			handler(v) {
				const defaults_applied = this.config?.defaults_applied;

				this.$nextTick(() => {
					this.tempConfig = v;
					this.tempConfig!.lineWidth = this.remoteColorByState.geometry.LineWidth;
					this.tempConfig!.pointSize = this.remoteColorByState.geometry.PointSize;
					this.setActiveSolidColor();
					this.tempConfig!.opacity = Math.round(
						this.remoteColorByState.geometry.Opacity * 100
					);

					if (this.config?.hasDataLoaded && !defaults_applied) {
						this.applyDefaultColorProperty();
					}

					if (
						v.selected &&
						v.type == "picks" &&
						this.config?.isBlastdog &&
						this.config?.hasDataLoaded &&
						!this.config?.defaults_applied
					) {
						this.applyDefaultPick();
					}
				});
			}
		},

		activeView() {
			const paneContainer: any = document.getElementById("pane-container");
			paneContainer.replaceChildren();
			// this.$nextTick(async ()=>{
			//         await this.updateHistogram();
			// })
		},
		colorColumn(val) {
			this.getColorType(val);
		},
		remoteColorByState: {
			deep: true,
			handler(v) {
				const colorConfig: any = this.customAnnotationColors;
				this.tempConfig!.lineWidth = this.remoteColorByState.geometry.LineWidth;
				this.tempConfig!.pointSize = this.remoteColorByState.geometry.PointSize;
				this.tLayerPointSize = this.remoteColorByState.geometry.PointSize;
				this.tempConfig!.opacity = Math.round(
					this.remoteColorByState.geometry.Opacity * 100
				); // multiple by 100 to show as a %

				//this.annotationTracking[this.remoteColorByState.coloring.field] = this.remoteColorByState.coloring.annotations;

				if (colorConfig) {
					const v_annotations_kv = this.annotation_array_to_kv(
						this.remoteColorByState.coloring.annotations
					);
					let diff = false;
					const keys = Object.keys(v_annotations_kv);
					for (const idx in keys) {
						const key = keys[idx];
						if (colorConfig.hasOwnProperty(key)) {
							if (
								v_annotations_kv[key] != colorConfig[key].label &&
								v_annotations_kv[key] != colorConfig[parseInt(key)].label
							) {
								diff = true;
								break;
							}
						}
					}
					if (diff) {
						const annotations = [];
						const indexedColors = [];
						const indexedOpacities = [];
						const size = this.remoteColorByState.coloring.annotations.length / 2;
						for (let i = 0; i < size; i++) {
							annotations.push(this.remoteColorByState.coloring.annotations[i * 2]);
							const annotationKey =
								this.remoteColorByState.coloring.annotations[i * 2 + 1];
							if (colorConfig[annotationKey]) {
								indexedColors.push(colorConfig[annotationKey].color[0]);
								indexedColors.push(colorConfig[annotationKey].color[1]);
								indexedColors.push(colorConfig[annotationKey].color[2]);
								annotations.push(colorConfig[annotationKey].label);
							} else if (colorConfig[parseInt(annotationKey)]) {
								indexedColors.push(colorConfig[parseInt(annotationKey)].color[0]);
								indexedColors.push(colorConfig[parseInt(annotationKey)].color[1]);
								indexedColors.push(colorConfig[parseInt(annotationKey)].color[2]);
								annotations.push(colorConfig[annotationKey].label);
							} else {
								indexedColors.push(
									this.remoteColorByState.coloring.indexedColors[i * 3 + 0]
								);
								indexedColors.push(
									this.remoteColorByState.coloring.indexedColors[i * 3 + 1]
								);
								indexedColors.push(
									this.remoteColorByState.coloring.indexedColors[i * 3 + 2]
								);
								annotations.push(
									this.remoteColorByState.coloring.annotations[i * 2]
								);
							}
							indexedOpacities.push(
								this.remoteColorByState.coloring.indexedOpacities[i]
							);
						}
						this.customColorAnnotation = {
							annotations,
							indexedColors,
							indexedOpacities
						};
						console.log(
							`Got remote color by update: set indexed colours to ${indexedColors} prior to colorByPush`
						);
						this.$nextTick(this.colorByPush);
					} else {
						this.customColorAnnotation = null;
					}
				}
				this.setActiveSolidColor();
				this.refreshKey++;
			}
		}
	},

	methods: {
		async selectProp($event: {
			label: string;
			value: string;
			type: "continuous" | "categorical";
			isTimeStamp?: boolean;
		}) {
			// mpd-4161 work-around - toggle filters for categorical blastdog props to allow returning of category list
			if (
				this.config?.isBlastdog &&
				this.allActiveFilters &&
				this.allActiveFilters.length > 0 &&
				$event.type === "categorical"
			) {
				const { indexedColors, field } = this.remoteColorByState.coloring;

				if (indexedColors.length > 0 && field == $event) {
					// on re-selecting the same field, properties won't update regardless of filters
					return;
				}
				for (const filter of this.allActiveFilters) {
					if (
						filter.layerName == this.tempConfig?.layerName &&
						(filter.ThresholdRange[0] > filter.absMin ||
							filter.ThresholdRange[1] < filter.absMax)
					) {
						// technically, the selected values (indexedColors) may have changed based off hierarchy selection, but due to another bug they don't update on property change regardless
						// (they only update when they are the current selection during the hierarchy change or on clicking refresh categories)
						await this.filtersStore.SSA_TEMP_DISABLE_FILTERS(filter.layerName);
						break;
					}
				}
			}
			const field = $event;
			// end of mpd-4161 work-around
			// console.log(
			// 	"User selected property ",
			// 	field.value,
			// 	", current selected property is ",
			// 	(this.config as any).selectedProperty,
			// 	", gas color property is ",
			// 	GAS_COLOR_PROPERTY_STR
			// );

			// Core selectProp functionality
			this.displayPaneStore.SET_PROPERTY(field, this.config?.layerIdentifier);
			this.setColorByField(field);
			// End of core selectProp functionality

			// TEMP!!  In the longer term, these shouldn't be visible in the prop selector.
			// Code is here for now, however, as selecting ioGAS Colors in the prop selector
			// should have the same effect as switching on the ioGAS switch
			const fieldName = field.value.trim();
			if (fieldName === GAS_COLOR_PROPERTY_STR) {
				// console.log("ioGAS colours ON");
				this.displayPaneStore.SET_USE_GAS_ATTRIBUTES(true, this.config?.layerIdentifier);
				this.displayPaneStore.SET_PREV_SELECTED_PROPERTY(
					this.config?.selectedProperty,
					this.tempConfig?.layerName
				);
			} else {
				this.displayPaneStore.SET_USE_GAS_ATTRIBUTES(false, this.config?.layerIdentifier);
			}
			// End of TEMP!! ioGas toggle syncing

			// Apply property defaults, if they haven't been for this prop and any are configured
			if (
				this.activeDataset.propertyDefaults &&
				!this.tempConfig?.propDefaultsApplied?.[fieldName]
			) {
				this.applyPropertySettings(fieldName, true);
			} else {
				this.applyPropertySettings(fieldName);
			}
		},
		loadDatasetState() {
			// console.log(
			// 	`🌻on activate dataset: isGAS = ${this.config?.isGAS}, useAtts= ${this.config?.useGASAttributes}, active layer `,
			// 	this.activeLayer
			// );
			this.applyDefaultSurface();
		},
		handleColorByChange(self: any, v: any) {
			if (v.colorType == "Categorical") return;
			if (v.preset != null && v.preset.id != null) {
				if (v.presetName != "" && v.presetName != null && v.preset) {
					if (
						self.presetsStore.SSA_PRESETS_ITEMS.map((item: any) => item.id).indexOf(
							v.preset.id
						) < 0
					) {
						self.presetsStore.SSA_SAVE_COLOR_PRESET(v.preset, false);
					}
					this.setColorScale(v.preset);
				}
			} else {
				const presetName = v.presetName;

				let preset;
				switch (presetName) {
					case "Rainbow Uniform":
						preset =
							self.presetsStore.SSA_PRESETS_ITEMS[
								self.presetsStore.SSA_PRESETS_ITEMS.findIndex((item: any) => {
									return item.id == "Rainbow Uniform";
								})
							];
						self.presetName = preset;
						self.displayPaneStore.SET_CONTINUOUS_COLOR_MAP(
							preset.id,
							preset,
							(self.tempConfig as any).layerIdentifier
						);
						break;
					case "dc_blue2cyan":
						preset =
							self.presetsStore.SSA_PRESETS_ITEMS[
								self.presetsStore.SSA_PRESETS_ITEMS.findIndex((item: any) => {
									return item.id == "Blue to Cyan";
								})
							];
						self.presetName = preset;
						self.displayPaneStore.SET_CONTINUOUS_COLOR_MAP(
							preset.id,
							preset,
							(self.tempConfig as any).layerIdentifier
						);
						break;
					case "greyscale":
						preset =
							self.presetsStore.SSA_PRESETS_ITEMS[
								self.presetsStore.SSA_PRESETS_ITEMS.findIndex((item: any) => {
									return item.id == "Greyscale";
								})
							];
						self.presetName = preset;
						self.displayPaneStore.SET_CONTINUOUS_COLOR_MAP(
							preset.id,
							preset,
							(self.tempConfig as any).layerIdentifier
						);
						break;
					default:
						preset =
							self.presetsStore.SSA_PRESETS_ITEMS[
								self.presetsStore.SSA_PRESETS_ITEMS.findIndex((item: any) => {
									return item.id == "Rainbow Uniform";
								})
							];

						self.presetName = preset;
						self.displayPaneStore.SET_CONTINUOUS_COLOR_MAP(
							preset.id,
							preset,
							(self.tempConfig as any).layerIdentifier
						);
						break;
				}
			}
			self.$nextTick(() => {
				if (self.colorType != "Discrete") {
					self.updateHistogram();
				}
			});
		},
		guidGenerator() {
			const S4 = () => {
				return (((1 + Math.random()) * 0x10000) | 0).toString(16).substring(1);
			};
			return S4() + S4() + "-" + S4() + "-" + S4() + "-" + S4() + "-" + S4() + S4() + S4();
		},
		invertRgbPoints() {
			if (this.presetIsFixed) return;
			const rgbPoints = this.remoteColorByState.coloring.rgbPoints;
			let stops = [] as any;
			const vals = [] as any;
			const colors = [] as any;

			for (let i = 0; i < rgbPoints.length; i += 4) {
				const [val, r, g, b] = [
					rgbPoints[i],
					rgbPoints[i + 1],
					rgbPoints[i + 2],
					rgbPoints[i + 3]
				] as any;
				colors.push([r, g, b]);
				vals.push(val);
			}
			colors.reverse();

			for (let k = 0; k < vals.length; k++) {
				stops = stops.concat([vals[k], ...colors[k]]);
			}
			return stops;
		},
		setActiveSolidColor() {
			const solidRGB = this.remoteColorByState.geometry.DiffuseColor;
			const componentToHex = (c: any) => {
				const hex = c.toString(16);
				return hex.length == 1 ? "0" + hex : hex;
			};
			const rgbToHex = (r: number, g: number, b: number) => {
				return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
			};
			if (solidRGB) {
				const diffuseHex = rgbToHex(
					solidRGB[0] * 255,
					solidRGB[1] * 255,
					solidRGB[2] * 255
				);
				this.activeSolidColor = diffuseHex;
			}
		},
		setUpdating(type: string) {
			setTimeout(() => {
				this.updating = type;
			}, 100);
		},
		finishedUpdating() {
			setTimeout(() => {
				this.updating = "";
			}, 400);
		},
		applyDefaultSurface() {
			this.$nextTick(() => {
				setTimeout(async () => {
					if (this.open && this.datasetType == "surface") {
						const surfaceDefaultProp = this.config?.availableProperties[0].children[0];
						if (surfaceDefaultProp) {
							await this.displayPaneStore.SET_PROPERTY(
								surfaceDefaultProp,
								this.config?.layerIdentifier
							);
							this.setColorByField(surfaceDefaultProp);
						}
					}
				}, 300);
			});
		},
		applyDefaultPick() {
			if (this.config?.selected && this.config?.defaults_applied == null) {
				const color = this.config?.color;
				this.$nextTick(async () => {
					let defProp = this.config?.pickProperty;
					if (this.config?.selectedProperty != null) {
						// The fix for MPD-4113 is swapping the selectedProperty label to value here - all other changes to default_properties are so that labels and values are available if needed, but shouldn't impact functionality
						defProp = this.config?.selectedProperty.value;
					}
					this.displayPaneStore.SET_PROPERTY(
						{ value: defProp, type: "continuous", label: defProp },
						this.tempConfig?.layerIdentifier
					);
					this.setColorByField({ value: defProp, type: "continuous", label: defProp });

					const { layerName } = this.config as any;
					const view = this.activeView;
					const componentToHex = (c: any) => {
						const hex = c.toString(16);
						return hex.length == 1 ? "0" + hex : hex;
					};
					const rgbToHex = (r: number, g: number, b: number) => {
						return "#" + componentToHex(r) + componentToHex(g) + componentToHex(b);
					};
					const rgbPointsToEdit = [];
					const n = 0;
					rgbPointsToEdit[n] = 0;
					rgbPointsToEdit[n + 1] = hex2float(rgbToHex(color.r, color.g, color.b))[0];
					rgbPointsToEdit[n + 2] = hex2float(rgbToHex(color.r, color.g, color.b))[1];
					rgbPointsToEdit[n + 3] = hex2float(rgbToHex(color.r, color.g, color.b))[2];
					rgbPointsToEdit[n + 4] = 1;
					rgbPointsToEdit[n + 5] = hex2float(rgbToHex(color.r, color.g, color.b))[0];
					rgbPointsToEdit[n + 6] = hex2float(rgbToHex(color.r, color.g, color.b))[1];
					rgbPointsToEdit[n + 7] = hex2float(rgbToHex(color.r, color.g, color.b))[2];
					const stateToPush = {
						layerName,
						fieldList: this.fieldList,
						viewId: view,
						field: defProp,
						column: defProp,
						rgbPoints: rgbPointsToEdit
					};

					await this.ssaStore.SSA_COLOR_BY_UPDATE(stateToPush);
				});
			} else {
				this.$nextTick(() => {
					this.setColorByField(this.tempConfig?.selectedProperty);
				});
			}
		},
		applyDefaultColorProperty(reapplyDefault = false) {
			const defaults_applied = this.config?.defaults_applied;
			this.$nextTick(() => {
				if (!this.config?.hasDataLoaded) return;
				if (
					this.config?.selected &&
					(!defaults_applied || reapplyDefault) &&
					this.config?.default_properties
				) {
					const availableProperties = (
						this.config as any
					).availableProperties[0].children.map((item: any) => {
						return item;
					});

					let defProp = "";

					for (let i = 0; i < this.config?.default_properties.length; i++) {
						if (
							availableProperties.findIndex(
								(item: { value: string; label: string }) =>
									item.value === this.config?.default_properties[i].value
								// 		|| // we could match on label here, but value should be sufficient and prevent column name changes from breaking functionality
								// item.label ===
								// 	this.config?.default_properties[i].label
							) != -1
						) {
							defProp = this.config?.default_properties[i].value;
							break;
						}
					}
					if (this.config?.selectedProperty != null) {
						defProp = this.config?.selectedProperty.value;
					}
					const propCheck:
						| {
								label: string;
								value: string;
								isTimeStamp: boolean;
								type: "continuous" | "categorical";
						  }
						| undefined = this.tempConfig?.availableProperties?.[0]?.children?.filter(
						(item: any) => item.value == defProp
					)[0];
					this.$nextTick(async () => {
						if (propCheck) {
							this.displayPaneStore.SET_PROPERTY(
								propCheck,
								this.tempConfig?.layerIdentifier
							);
							this.setColorByField(propCheck);
						}

						let tries = 0;
						while (this.remoteColorByState.coloring.field == "") {
							tries++;
							if (tries > 10) {
								break;
							}
							await sleep(1000);
						}

						if (
							this.config?.colorMap != null &&
							this.config?.colorType === "Continuous" // prevents a double update when the default property is categorical
						) {
							this.presetName = this.config?.colorMap;
						}
						if (this.config?.scaleType != null) {
							this.colorType = this.config?.scaleType;
						}

						if (
							this.activeDataset.propertyDefaults &&
							propCheck &&
							!this.tempConfig?.propDefaultsApplied?.[propCheck.value]
						) {
							this.applyPropertySettings(propCheck.value, true);
						}
					});

					if (propCheck) return;
				}
				if (
					this.config?.selected &&
					(this.datasetType == "drillhole" ||
						this.datasetType == "blockmodel" ||
						this.datasetType == "points" ||
						this.config?.layerType == "interval") &&
					(!defaults_applied || reapplyDefault)
				) {
					this.$nextTick(() => {
						for (
							let i = 0;
							i <
							this.activeDataset!.datasetColumnMapping.filter(
								(item: any) => item.layerIdentifier == this.config?.layerIdentifier
							).length;
							i++
						) {
							const dataset = this.activeDataset!.datasetColumnMapping.filter(
								(item: any) => item.layerIdentifier == this.config?.layerIdentifier
							);
							if (dataset[i].isCategorical == "false") {
								this.$nextTick(() => {
									this.displayPaneStore.SET_PROPERTY(
										{
											value: dataset[i].column,
											label: dataset[i].label,
											type:
												dataset[i].isCategorical == "false"
													? "continuous"
													: "categorical"
										},
										this.tempConfig?.layerIdentifier
									);

									this.setColorByField({
										value: dataset[i].column,
										type:
											dataset[i].isCategorical == "false"
												? "continuous"
												: "categorical"
									});
								});
								if (
									this.activeDataset.propertyDefaults &&
									!this.tempConfig?.propDefaultsApplied?.[dataset[i].column]
								) {
									this.applyPropertySettings(dataset[i].column, true);
								}
								break;
							}
						}
					});

					return;
				}
				//  base case if defaults are already applied and we end up here
				this.setColorByField(this.tempConfig?.selectedProperty);

				return;
			});
		},
		applyPropertySettings(property: string, applyingDefault = false) {
			const defaultOpacity = 100;
			let defaultSize = 3; // ideally we should never use this, but it allows persistence of existing behavior when nothing is configured
			if (this.isPick || this.config?.layerIdentifier.includes("collar/")) defaultSize = 10;
			if (this.config?.layerIdentifier.includes("survey/")) defaultSize = 1;

			this.applyingPropertySettings = true;
			let propSettings: PropertyDefaults | PropertySettings | null = null;

			if (applyingDefault) {
				propSettings = this.activeDataset?.propertyDefaults?.find(
					(propDefault: PropertyDefaults) =>
						property === propDefault.name &&
						(propDefault.layerIdentifier
							? this.config?.layerIdentifier === propDefault.layerIdentifier
							: true)
				);
				this.displayPaneStore.SSA_SET_APPLIED_PROPERTY_DEFAULT(
					this.config?.layerName,
					property
				);
			} else {
				propSettings = this.tempConfig?.currentPropSettings?.[property];
			}
			this.layerOpacity = propSettings?.opacity ?? defaultOpacity;

			if (this.tempConfig?.layerType === "point") {
				this.layerPointSize = propSettings?.pointSize ?? defaultSize;
			} else if (this.tempConfig?.layerType === "interval") {
				this.layerLineWidth = propSettings?.lineWidth ?? defaultSize;
			} else if (!this.tempConfig?.layerType) {
				this.layerPointSize = propSettings?.pointSize ?? defaultSize;
			}

			// apply continuous coloring - will use a color map if it exists. To ignore, assign colorMap default config to ""
			// Or we could wholesale replace default settings in the API rather than merge with Object.assign them as we do currently
			// if (propSettings.colorMap) {
			// 	const presetsItem = this.presetsStore.SSA_PRESETS_ITEMS.find((item: any) => {
			// 		return item.id == propSettings.colorMap;
			// 	});
			// 	console.log(`🚀🤗 ~ presetsItem ~ presetsItem:`, presetsItem);
			// 	if (presetsItem) this.presetName = presetsItem;
			// } else if (propSettings.customContinuousColors) {
			// 	console.log(
			// 		`🚀🤗 ~ applyPropertySettings ~ propSettings.customContinuousColors:`,
			// 		propSettings.customContinuousColors
			// 	);
			// 	// this.presetsStore.SSA_SAVE_COLOR_PRESET()
			// 	// 			fixedColorPresets: [
			// 	// {
			// 	// 	id: i18n.global.t("ssa.displayPane.colorGeneral.newFixedGradient"),
			// 	// 	rgbPoints: [
			// 	// 		25, 1, 1, 1, 50, 1, 1, 1, 50, 1, 0, 0, 100, 1, 0, 0, 100, 0, 0, 1, 255, 0,
			// 	// 		0, 1
			// 	// 	],
			// 	// 	value: i18n.global.t("ssa.displayPane.colorGeneral.newFixedGradient"),
			// 	// 	text: i18n.global.t("ssa.displayPane.colorGeneral.newFixedGradient"),
			// 	// 	type: "fixed",
			// 	// 	annotations: ["37.5", "white", "75", "red", "177.5", "blue"]
			// 	// },
			// } else if (propSettings.customContinuousFixedColors) {
			// 	console.log(
			// 		`🚀🤗 ~ applyPropertySettings ~ propSettings.customContinuousFixedColors:`,
			// 		propSettings.customContinuousFixedColors
			// 	);
			// }

			// // apply categorical coloring (tentatively, convert customColorAnnotation to non-computed and reassign)
			// if ("customCategoricalColors" in propSettings && propSettings.customCategoricalColors) {
			// 	console.log(
			// 		`🚀🤗 ~ applyPropertySettings ~ propSettings.customCategoricalColors:`,
			// 		propSettings.customCategoricalColors
			// 	);
			// }
			// save applied propSettings to the store
			this.displayPaneStore.SSA_SET_PROPERTY_SETTING(
				this.config?.layerName,
				property,
				propSettings
			);
			this.applyingPropertySettings = false;
		},
		invertPreset() {
			const presetInvert = !this.remoteColorByState.coloring.presetInvert;
			if (this.presetIsFixed) return;
			this.colorBy = { presetInvert, rgbPoints: this.invertRgbPoints() };
			this.$nextTick(this.colorByPush);
		},
		snapColorRange() {
			const layerName = this.config?.layerName;
			const viewId = this.activeStore.view;
			const { field } = this.colorBy;

			this.ssaStore.SSA_COLOR_BY_UPDATE({
				layerName,
				viewId,
				fieldList: this.fieldList,
				field,
				colorRange: [
					Math.round(this.minMagicRange * 1000) / 1000,
					Math.round(this.maxMagicRange * 1000) / 1000
				]
			});
		},
		resizeContainer() {
			this.$nextTick(() => {
				if (this.$refs.cbContainer) {
					const ro = new ResizeObserver(() => {
						if (this.$refs.cbContainer) {
							const { width } = (
								this.$refs.cbContainer as any
							).getBoundingClientRect();
							return (this.displayPaneWidth = Number(width.toFixed(0)));
						}
					});

					// Observe one or multiple elements
					ro.observe(this.$refs.cbContainer as any);
				}
			});
		},
		async updateHistogram() {
			this.$nextTick(async () => {
				if (this.isCategorical) {
					return;
				}
				const layerName = this.config?.layerName;
				const viewId = this.activeStore.view;
				const { field } = this.colorBy;
				if (!field) return;

				// Hierarchy are categorical color with no histogram
				if (field.indexOf("hierarchy") !== -1) {
					this.histogramData = null;
					return;
				}

				if (field) {
					this.updatingHistogram = true;
					const dataRange = this.histogramDataRange;
					const numberOfSamples = this.useLogScale ? 500 : 100;
					const data = await this.fetchHistogram({
						layerName,
						viewId,
						field,
						dataRange,
						numberOfSamples
					});

					if (data != undefined) {
						const { histogram, removed } = data;
						this.histogramData = histogram;
						this.histogramRemoved = removed;
					} else {
						this.histogramData = null;
						this.histogramRemoved = [0, 0];
					}

					this.updatingHistogram = false;
				} else {
					this.histogramData = null;
					this.histogramRemoved = [0, 0];
				}
			});
		},
		getColorType(val: any) {
			if (val == "" || val == null) return;
			if (val.indexOf("str") > -1) {
				this.colorTypeItems = this.toItemsWithText(["Categorical"], COLOR_TYPE_LABELS);
				if (this.colorType !== "Categorical") {
					this.colorType = "Categorical";
				}
			} else {
				const categorical =
					this.activeStore.SSA_ACTIVE_DATASET &&
					this.activeStore.SSA_ACTIVE_DATASET.datasetColumnMapping &&
					this.activeStore.SSA_ACTIVE_DATASET.datasetColumnMapping.filter((item: any) => {
						if (item.column == val) {
							if (item.isCategorical == "true") {
								return true;
							}
						}
					});

				if (categorical && categorical.length > 0) {
					this.colorTypeItems = this.toItemsWithText(["Categorical"], COLOR_TYPE_LABELS);
					this.colorType = "Categorical";
				} else {
					this.colorTypeItems = this.toItemsWithText(
						["Continuous", "Logarithmic", "Discrete"],
						COLOR_TYPE_LABELS
					);
				}
			}
		},
		// async updateAnnotationsAndColors(nbSteps: any) {
		// 	const annotationsToEdit: any = [];
		// 	const rgbPointsToEdit: any = [];
		// 	const { layerName, view: viewId } = this.remoteColorByState;
		// 	const {
		// 		field,
		// 		colorRange: [min, max],
		// 		annotations
		// 	} = this.remoteColorByState.coloring;

		// 	let { rgbPoints } = this.remoteColorByState.coloring;

		// 	if (rgbPoints == null) {
		// 		rgbPoints = [];
		// 	}
		// 	if (rgbPoints.length > 0) {
		// 		return;
		// 	}
		// 	const step = (max - min) / nbSteps;
		// 	const nbServerColors = this.isPick ? 0 : rgbPoints.length / 8 - 1;
		// 	const epsilon = step / 1000;

		// 	for (let i = 0; i < nbSteps; i++) {
		// 		const value = min + step * i;

		// 		annotationsToEdit.push(this.toAnnotationValue(value + step * 0.5));
		// 		annotationsToEdit.push(annotations[i * 2 + 1] || "");

		// 		rgbPointsToEdit.push(value + epsilon);
		// 		if (i < nbServerColors) {
		// 			rgbPointsToEdit.push(rgbPoints[i * 8 + 1]);
		// 			rgbPointsToEdit.push(rgbPoints[i * 8 + 2]);
		// 			rgbPointsToEdit.push(rgbPoints[i * 8 + 3]);
		// 		} else {
		// 			rgbPointsToEdit.push(DISCRETE_COLORS[i][0]);
		// 			rgbPointsToEdit.push(DISCRETE_COLORS[i][1]);
		// 			rgbPointsToEdit.push(DISCRETE_COLORS[i][2]);
		// 		}

		// 		rgbPointsToEdit.push(value + step - epsilon);
		// 		if (i < nbServerColors) {
		// 			rgbPointsToEdit.push(rgbPoints[i * 8 + 1]);
		// 			rgbPointsToEdit.push(rgbPoints[i * 8 + 2]);
		// 			rgbPointsToEdit.push(rgbPoints[i * 8 + 3]);
		// 		} else {
		// 			rgbPointsToEdit.push(DISCRETE_COLORS[i][0]);
		// 			rgbPointsToEdit.push(DISCRETE_COLORS[i][1]);
		// 			rgbPointsToEdit.push(DISCRETE_COLORS[i][2]);
		// 		}
		// 	}
		// 	await this.ssaStore.SSA_COLOR_BY_UPDATE({
		// 		layerName,
		// 		//colorType: "Discrete",
		// 		viewId,
		// 		field,
		// 		fieldList: this.fieldList,
		// 		rgbPoints: rgbPointsToEdit,
		// 		annotations: annotationsToEdit
		// 	});
		// 	return Promise.resolve();
		// },
		// discreteColors() {
		// 	const { annotations, rgbPoints } = this.remoteColorByState.coloring;
		// 	const list = [];
		// 	const size = annotations.length / 2;
		// 	for (let i = 0; i < size; i++) {
		// 		const rgb =
		// 			i * 8 < rgbPoints.length
		// 				? rgbPoints.slice(i * 8 + 1, i * 8 + 4)
		// 				: DISCRETE_COLORS[i % DISCRETE_COLORS.length];
		// 		const colorEntry = {
		// 			value: annotations[i * 2],
		// 			annotation: annotations[i * 2 + 1] || "",
		// 			rgb,
		// 			hex: `#${rgb
		// 				.map((i: any) =>
		// 					Math.round(256 + i * 255)
		// 						.toString(16)
		// 						.substring(1)
		// 				)
		// 				.join("")}`
		// 		};

		// 		if (this.localAnnotations) {
		// 			colorEntry.annotation =
		// 				this.localAnnotations[i * 2 + 1] || colorEntry.annotation;
		// 		}

		// 		list.push(colorEntry);
		// 	}

		// 	list.reverse();

		// 	return list;
		// },
		async setColorByField(field: any) {
			if (field == null) return;

			this.colorBy = {
				rgbPoints: this.evaluatedRgbPoints.length
					? this.evaluatedRgbPoints
					: this.presetName.rgbPoints,
				field: field.value,
				categorical: field.type == "categorical",
				colorType: field.type == "categorical" ? "Categorical" : "Continuous"
			};

			this.$nextTick(async () => {
				await this.colorByPush();
				return Promise.resolve();
			});
		},
		annotation_array_to_kv(array: any) {
			if (array == null) return;

			const kv: any = {};
			for (let i = 0; i < array.length / 2; i++) {
				kv[array[i * 2]] = array[i * 2 + 1];
			}
			return kv;
		},
		getFieldRange(field: any): any {
			if (this.fieldRanges[field]) {
				const { min, max } = this.fieldRanges[field];
				return [min, max];
			}
			return [1, -1];
		},
		async colorByPush() {
			if (this.localColorByState || this.customColorAnnotation) {
				const { field } = this.remoteColorByState.coloring;

				const state: any = Object.assign(
					{
						layerName: this.config?.layerName,
						viewId: this.activeStore.view,
						field,
						fieldList: this.fieldList,
						colorRange: this.localColorByState.colorRange,
						preset: this.remoteColorByState.coloring.preset
					},
					this.localColorByState,
					this.customColorAnnotation
				);

				if (!state.colorType && !this.active && !field) {
					// if active, allow swapping of color map, but otherwise we can safely return early. Only needed for default properties for BD collars that are categorical
					return;
				}

				if (state.colorRange != null) {
					if (state.colorRange[0] == 1 && state.colorRange[1] == -1) {
						delete state.colorRange;
					}
				}
				if (state.presetName == null) {
					state.presetName = this.presetName.id;
					state.preset = this.presetName;
				}
				if (state.annotations == null) {
					state.annotations = [];
				}
				if (state.type == "fixed") {
					state.annotations = this.localColorByState.annotations;
				} else {
					state.annotations = [];
				}
				this.localColorByState = null;
				if (state.colorType == "Categorical") {
					delete state.annotations;
					delete state.rgbPoints;

					delete state.presetName;
				}

				if (
					this.activeStore.SSA_ACTIVE_DATASET &&
					this.activeStore.selectedDatasets[this.activeView] &&
					this.activeStore.selectedDatasets[this.activeView][
						this.activeStore.SSA_ACTIVE_DATASET.id
					] &&
					state.layerName.includes(this.activeStore.SSA_ACTIVE_DATASET.id)
				) {
					if (!this.isPick) {
						await this.ssaStore.SSA_COLOR_BY_UPDATE(state);
						this.displayPaneStore.SET_CONTINUOUS_COLOR_MAP(
							state.preset.id,
							state.preset,
							this.tempConfig?.layerIdentifier
						);
						await this.ssaStore.SSA_COLOR_BY_UPDATE(state); // have to call again? not sure why
						setTimeout(() => {
							this.updateHistogram();
							return;
						}, 100);
					}
				} else {
					return;
				}
			} else {
				this.localColorByState = null;
				return;
			}
		},

		colorByReset() {
			let colorRange = this.getFieldRange(this.colorBy.field);
			if (
				this.activeRepresentationState.filters &&
				this.activeRepresentationState.filters.length > 0
			) {
				const filters = this.activeRepresentationState.filters.map((item: any) => {
					return item.Scalars[1];
				});
				if (filters.indexOf(this.colorBy.field) > -1) {
					colorRange = [
						this.activeRepresentationState.filters[filters.indexOf(this.colorBy.field)]
							.ThresholdRange[0],
						this.activeRepresentationState.filters[filters.indexOf(this.colorBy.field)]
							.ThresholdRange[1]
					];
				}
			}
			if (colorRange[1] > colorRange[0]) {
				this.colorBy = { colorRange };
				this.$nextTick(this.colorByPush);
			} else if (this.localColorByState) {
				delete this.localColorByState.colorRange;
				this.$nextTick(this.colorByPush);
			}
		},
		setLayerOpacity: useDebounceFn(function (v: number, vue) {
			if (typeof v != "number") return;
			vue.ssaStore.SSA_REPRESENTATION_UPDATE_PROPERTY({
				layerName: (vue.config as any).layerName,
				viewId: vue.activeStore.view,
				type: "geometry",
				name: "Opacity",
				value: v / 100
			});
		}, 100),
		setEdgeVisibility(v: any): any {
			if (typeof v != "boolean") return;
			const value = v ? "Surface With Edges" : "Surface";
			this.ssaStore.SSA_REPRESENTATION_UPDATE_PROPERTY({
				layerName: this.config?.layerName,
				viewId: this.activeStore.view,
				type: "geometry",
				name: "Representation",
				value
			});
		},
		setLayerPointSize: useDebounceFn(function (v: number, vue) {
			let name = "PointSize";

			// if (this.tempConfig?.layerType === "point") name = "PointSize"; // default
			if (
				vue.tempConfig?.layerType === "interval" ||
				vue.activeStore?.SSA_ACTIVE_LAYER?.toLowerCase()?.indexOf("survey") > -1
			)
				name = "LineWidth";
			if (typeof v != "number") return;
			vue.ssaStore.SSA_REPRESENTATION_UPDATE_PROPERTY({
				layerName: (vue.config as any).layerName,
				viewId: vue.activeStore.view,
				type: "geometry",
				name,
				value: v
			});
			vue.ssaStore.SSA_REPRESENTATION_UPDATE_PROPERTY({
				layerName: (vue.config as any).layerName,
				viewId: vue.activeStore.view,
				type: "geometry",
				name: "PointSize",
				value: v
			});
		}, 100),
		setLayerLineWidth: useDebounceFn(function (v: number, vue) {
			const name = "LineWidth";
			// if (this.tempConfig?.layerType === "point") name = "PointSize"; // default

			if (typeof v != "number") return;
			vue.ssaStore.SSA_REPRESENTATION_UPDATE_PROPERTY({
				layerName: (vue.config as any).layerName,
				viewId: vue.activeStore.view,
				type: "geometry",
				name,
				value: v
			});
		}, 100),
		toggleCellOutline(): any {
			this.cellOutline = !this.cellOutline;
		},
		pickSolidColor({ color }: { color: string; name: string }): any {
			const colorRGB = hexToRgb(color);
			this.activeSolidColor = color;
			this.ssaStore.SSA_REPRESENTATION_UPDATE_PROPERTY({
				layerName: this.config?.layerName,
				viewId: this.activeStore.view,
				type: "geometry",
				name: "DiffuseColor",
				value: [colorRGB.r / 255, colorRGB.g / 255, colorRGB.b / 255]
			});
		},

		openPropSelector() {
			this.propSelectorOpen = !this.propSelectorOpen;
			const container: any = window.document.getElementById("pane-container");
			if (this.propSelectorOpen) {
				this.$nextTick(() => {
					window.addEventListener(
						"mousedown",
						((this.outsideClickListener as any) = (e: any) => {
							const [x, y] = [e.pageX, e.pageY];
							const elements = document.elementsFromPoint(x, y);
							for (let i = 0; i < elements.length; i++) {
								if (elements[i].classList.contains("prop-selector")) {
									return;
								}
							}
							this.propSelectorOpen = false;
							window.removeEventListener(
								"mousedown",
								this.outsideClickListener as any
							);
						})
					);
					const { left, top, width } = (
						this.$refs as any
					).propSelectorAnchor.getBoundingClientRect();
					const { height: elHeight } = (
						this.$refs as any
					).propSelector.$el.getBoundingClientRect();
					if (left - 250 < 0) {
						(this.$refs as any).propSelector.$el.style.left = left + width + "px";
					} else {
						(this.$refs as any).propSelector.$el.style.left = left - 250 + "px";
					}
					if (top + elHeight > window.innerHeight) {
						(this.$refs as any).propSelector.$el.style.top =
							window.innerHeight - elHeight - 15 + "px";
					} else {
						(this.$refs as any).propSelector.$el.style.top = top + "px";
					}

					container.appendChild((this.$refs as any).propSelector.$el);
				});
			}
		},
		openColorPicker(colorName: any) {
			this.colorPickerOpen = !this.colorPickerOpen;
			const container: any = window.document.getElementById("pane-container");
			if (this.colorPickerOpen) {
				this.$nextTick(() => {
					window.addEventListener(
						"mousedown",
						((this.outsideClickListener as any) = (e: any) => {
							const [x, y] = [e.pageX, e.pageY];
							const elements = document.elementsFromPoint(x, y);
							for (let i = 0; i < elements.length; i++) {
								if (elements[i].classList.contains("color-picker")) {
									return;
								}
							}
							this.colorPickerOpen = false;
							window.removeEventListener(
								"mousedown",
								this.outsideClickListener as any
							);
						})
					);

					const { left, top, width } = (
						this.$refs[colorName] as any
					)[0].getBoundingClientRect();
					const { height: elHeight } = (
						this.$refs.colorPicker as any
					).$el.getBoundingClientRect();

					if (left - 250 < 0) {
						(this.$refs as any).colorPicker.$el.style.left = left + width + "px";
					} else {
						(this.$refs as any).colorPicker.$el.style.left = left - 250 + "px";
					}
					if (top + elHeight > window.innerHeight) {
						(this.$refs as any).colorPicker.$el.style.top =
							window.innerHeight - elHeight - 15 + "px";
					} else {
						(this.$refs as any).colorPicker.$el.style.top = top + "px";
					}

					container.appendChild((this.$refs.colorPicker as any).$el);
				});
			}
		},
		openSolidColorPicker() {
			this.solidColorPickerOpen = !this.solidColorPickerOpen;
			const container: any = window.document.getElementById("pane-container");
			if (this.solidColorPickerOpen) {
				this.$nextTick(() => {
					window.addEventListener(
						"mousedown",
						((this.outsideClickListener as any) = (e: any) => {
							const [x, y] = [e.pageX, e.pageY];
							const elements = document.elementsFromPoint(x, y);
							for (let i = 0; i < elements.length; i++) {
								if (elements[i].classList.contains("color-picker")) {
									return;
								}
							}
							this.solidColorPickerOpen = false;
							window.removeEventListener(
								"mousedown",
								this.outsideClickListener as any
							);
						})
					);

					const { left, top, width } = (
						this.$refs.solidColorPickerBlock as any
					).getBoundingClientRect();
					const { height: elHeight } = (
						this.$refs.solidColorPicker as any
					).$el.getBoundingClientRect();

					if (left - 250 < 0) {
						(this.$refs as any).solidsolidColorPicker.$el.style.left =
							left + width + "px";
					} else {
						(this.$refs as any).solidColorPicker.$el.style.left = left - 250 + "px";
					}
					if (top + elHeight > window.innerHeight) {
						(this.$refs as any).solidColorPicker.$el.style.top =
							window.innerHeight - elHeight - 15 + "px";
					} else {
						(this.$refs as any).solidColorPicker.$el.style.top = top + "px";
					}

					container.appendChild((this.$refs.solidColorPicker as any).$el);
				});
			}
		},
		openColorMapSelector() {
			this.colorMapSelectorOpen = !this.colorMapSelectorOpen;
			const container: any = window.document.getElementById("pane-container");

			if (this.colorMapSelectorOpen) {
				this.$nextTick(() => {
					window.addEventListener(
						"mousedown",
						((this.outsideClickListener as any) = (e: any) => {
							const [x, y] = [e.pageX, e.pageY];
							const elements = document.elementsFromPoint(x, y);
							for (let i = 0; i < elements.length; i++) {
								if (elements[i].classList.contains("map-selector")) {
									return;
								}
							}
							this.colorMapSelectorOpen = false;
							window.removeEventListener(
								"mousedown",
								this.outsideClickListener as any
							);
						})
					);
					const { left, top, width } = (
						this.$refs.colorMapSelectorAnchor as any
					).getBoundingClientRect();

					const { height: elHeight } = (
						this.$refs.colorMapSelector as any
					).$el.getBoundingClientRect();

					if (left - 350 < 0) {
						(this.$refs as any).colorMapSelector.$el.style.left = left + width + "px";
					} else {
						(this.$refs as any).colorMapSelector.$el.style.left = left - 350 + "px";
					}
					if (top + elHeight > window.innerHeight) {
						(this.$refs as any).colorMapSelector.$el.style.top =
							window.innerHeight - elHeight - 15 + "px";
					} else {
						(this.$refs as any).colorMapSelector.$el.style.top = top + "px";
					}

					container.appendChild((this.$refs.colorMapSelector as any).$el);
				});
			}
		},
		handleDisplayPaneUnselectedOnSaveState() {
			if (
				this.tempConfig?.selectedProperty &&
				this.tempConfig?.selectedProperty?.type === "continuous"
			) {
				const preset =
					this.presetsStore.SSA_PRESETS_ITEMS[
						this.presetsStore.SSA_PRESETS_ITEMS.findIndex((item: any) => {
							return (
								item.id ==
								(this.tempConfig?.preset?.id ?? this.tempConfig?.colorMap)
							);
						})
					];
				this.presetName = preset;
				this.displayPaneStore.SET_CONTINUOUS_COLOR_MAP(
					preset.id,
					preset,
					this.config?.layerIdentifier
				);
			}

			this.displayPaneStore.SET_PROPERTY(
				this.tempConfig?.selectedProperty,
				this.tempConfig?.layerIdentifier
			);
			this.setColorByField((this.tempConfig as any).selectedProperty);
			if (
				this.tempConfig?.selectedProperty &&
				this.tempConfig?.selectedProperty?.type === "continuous"
			) {
				let tries = 0;
				// needs an arbitrary delay - not an ideal solution but I don't see any alternative
				this.$nextTick(async () => {
					while (tries < 10) {
						const colorRange = this.getFieldRange(this.colorBy.field);
						if (colorRange[0] != 1 && colorRange[1] != -1) {
							this.colorByReset();
							break;
						}

						tries++;
						if (tries > 5) {
							break;
						}
						await sleep(250);
					}
				});
			}
			delete this.tempConfig?.displayPaneLayerUnselectedOnSaveState; // # todo - add store action to remove this, we shouldn't be directly modifying the store
		}
	}
});
